import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Eye, Power, Trash2, RefreshCw, AlertCircle, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const PlanBadge = ({ plan }) => {
  const variants = {
    clinica: 'default',
    solo: 'secondary',
    starter: 'outline',
  };
  const labels = { clinica: 'Clínica', solo: 'Solo', starter: 'Starter' };
  return <Badge variant={variants[plan] || 'outline'}>{labels[plan] || plan || 'Starter'}</Badge>;
};

const StatusBadge = ({ user }) => {
  if (user.active === false) {
    return <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Desativado</Badge>;
  }
  if (user.subscription_status === 'past_due') {
    return <Badge variant="outline" className="bg-amber-100 text-amber-800">Inadimplente</Badge>;
  }
  if (user.subscription_status === 'canceled') {
    return <Badge variant="outline" className="bg-gray-200 text-gray-700">Cancelado</Badge>;
  }
  return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Ativo</Badge>;
};

const ConfirmDeleteModal = ({ open, user, onClose, onConfirm, busy }) => {
  if (!open || !user) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="delete-user-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/40">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Excluir usuário</h3>
            <p className="text-sm text-gray-500 mt-1">
              Esta ação é <strong>irreversível</strong>. Todos os dados de <strong>{user.email}</strong>{' '}
              (pacientes, consultas, transcrições) serão apagados.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="cancel-delete-btn">Cancelar</Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="confirm-delete-btn"
          >
            {busy ? 'Excluindo...' : 'Sim, excluir'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isFounder = Boolean(authUser?.is_founder);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [busyRow, setBusyRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.admin.getActivity();
      setUsers(data.activity || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, query]);

  const handleToggleActive = async (u) => {
    const userId = u.user_id || u.id;
    const isActive = u.active !== false;
    if (!window.confirm(`${isActive ? 'Desativar' : 'Reativar'} ${u.email}?`)) return;
    setBusyRow(userId);
    try {
      await api.admin.setUserStatus(userId, !isActive);
      toast.success(isActive ? 'Usuário desativado' : 'Usuário reativado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status');
    } finally {
      setBusyRow(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const userId = deleteTarget.user_id || deleteTarget.id;
    setDeleting(true);
    try {
      await api.admin.deleteUser(userId);
      toast.success('Usuário excluído permanentemente');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-users-title">Usuários</h1>
            <p className="text-gray-500">Gerenciar contas, assinaturas e ativações</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="admin-users-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Lista de usuários</CardTitle>
            <CardDescription>{users.length} cadastrados · {filtered.length} exibidos</CardDescription>
            <div className="relative mt-3 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome ou email..."
                className="pl-10 pr-9"
                data-testid="admin-users-search"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-2" />
                <p className="text-red-500">{error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Nome</th>
                      <th className="text-left py-3 px-2">Email</th>
                      <th className="text-left py-3 px-2">Plano</th>
                      <th className="text-left py-3 px-2">Cadastro</th>
                      <th className="text-left py-3 px-2">Status</th>
                      <th className="text-right py-3 px-2">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const userId = u.user_id || u.id;
                      const isActive = u.active !== false;
                      const busy = busyRow === userId;
                      return (
                        <tr key={userId} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`admin-user-row-${userId}`}>
                          <td className="py-3 px-2 font-medium">{u.full_name || '—'}</td>
                          <td className="py-3 px-2 text-gray-500">{u.email}</td>
                          <td className="py-3 px-2"><PlanBadge plan={u.plan_type} /></td>
                          <td className="py-3 px-2 text-xs text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td className="py-3 px-2"><StatusBadge user={u} /></td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => navigate(`/admin/users/${userId}/consultations`)}
                                title="Ver consultas"
                                data-testid={`view-consultations-${userId}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                disabled={busy}
                                onClick={() => handleToggleActive(u)}
                                title={isActive ? 'Desativar' : 'Reativar'}
                                data-testid={`toggle-active-${userId}`}
                              >
                                <Power className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-red-500'}`} />
                              </Button>
                              {isFounder && (
                                <Button
                                  variant="ghost" size="sm"
                                  disabled={busy}
                                  onClick={() => setDeleteTarget(u)}
                                  title="Excluir usuário"
                                  data-testid={`delete-user-${userId}`}
                                  className="hover:bg-red-100 dark:hover:bg-red-900/30"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Nenhum usuário encontrado.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        user={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        busy={deleting}
      />
    </AdminLayout>
  );
}
